import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Checkbox, Button, Group, Text, Stack, Box } from '@mantine/core';
import { DatePickerInput, TimePicker } from '@mantine/dates';
import { CiCalendar } from 'react-icons/ci';
import { IoTimeOutline } from 'react-icons/io5';
import { useQueryClient } from '@tanstack/react-query';
import { AvailabilityFormData } from '@worker/types/myJobs.type';
import { useWorkerStore } from '@worker/stores';
import { saveUnavailableDates } from '@worker/services/MyJobs/api';
import { isMobile } from 'react-device-detect';
import useNotification from '@hooks/useNotification';
import { extractErrorMessage } from '@worker/helpers/my-jobs-helpers';

interface AvailabilityFormProps {
  isSubmitting?: boolean;
}

export default function AvailabilityForm({
  isSubmitting = false,
}: AvailabilityFormProps) {
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    clearErrors,
    formState,
  } = useForm<AvailabilityFormData>({
    defaultValues: {
      dateRange: [null, null],
      isAllDay: false,
      startTime: '',
      endTime: '',
    },
    mode: 'onSubmit',
  });

  const isAllDay = watch('isAllDay');
  const isFormSubmitting = formState.isSubmitting || isSubmitting;
  const { user } = useWorkerStore();
  const queryClient = useQueryClient();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  // Minimum date is tomorrow - can't mark today as unavailable since it already started
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const errorTextStyles = {
    position: 'absolute' as const,
    left: 0,
    bottom: -18,
    whiteSpace: 'nowrap' as const,
    fontSize: 12,
  };

  useEffect(() => {
    if (isAllDay) {
      setValue('startTime', '');
      setValue('endTime', '');
      clearErrors(['startTime', 'endTime']);
    }
  }, [isAllDay, setValue, clearErrors]);

  const handleFormSubmit = async (data: AvailabilityFormData) => {
    if (!data.dateRange[0] || !data.dateRange[1] || !user?.id) return;

    try {
      const startDate =
        data.dateRange[0] instanceof Date
          ? data.dateRange[0]
          : new Date(data.dateRange[0]);
      const endDate =
        data.dateRange[1] instanceof Date
          ? data.dateRange[1]
          : new Date(data.dateRange[1]);

      // Mantine stores dates as UTC midnight, extract the UTC date (what user selected)
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      const apiPayload = {
        isAllDay: data.isAllDay,
        startDate: startDateStr,
        endDate: endDateStr,
        ...(data.isAllDay
          ? {}
          : { startTime: data.startTime, endTime: data.endTime }),
      };

      await saveUnavailableDates(apiPayload);
      queryClient.invalidateQueries({ queryKey: ['worker-availabilities'] });

      reset();
      showSuccessNotification('Unavailable dates saved successfully');
    } catch (error) {
      showErrorNotification(
        extractErrorMessage(
          error,
          'Failed to save unavailable dates. Please try again.'
        )
      );
    }
  };

  // Define inputs once as constants to avoid duplication
  const dateRangeInput = (
    <Controller
      name="dateRange"
      control={control}
      rules={{
        validate: (value) => {
          if (!value || !value[0] || !value[1]) {
            return 'Date range is required';
          }
          return true;
        },
      }}
      render={({ field, fieldState }) => (
        <Box pos="relative">
          <DatePickerInput
            leftSection={<CiCalendar size={20} color="black" />}
            placeholder="Select date range"
            valueFormat="MM/DD/YY"
            type="range"
            clearable
            allowSingleDateInRange
            minDate={tomorrow}
            value={field.value}
            onChange={field.onChange}
            error={!!fieldState.error}
          />
          {fieldState.error?.message && (
            <Text c="red" style={errorTextStyles}>
              {fieldState.error.message}
            </Text>
          )}
        </Box>
      )}
    />
  );

  const allDayCheckbox = (
    <Controller
      name="isAllDay"
      control={control}
      render={({ field }) => (
        <Checkbox
          label="All Day"
          checked={field.value}
          onChange={(event) => field.onChange(event.currentTarget.checked)}
        />
      )}
    />
  );

  const timeRangeInputs = (
    <>
      <Controller
        name="startTime"
        control={control}
        rules={{
          required: !isAllDay ? 'Start time is required' : false,
        }}
        render={({ field, fieldState }) => (
          <Box pos="relative" style={{ flex: isMobile ? 1 : undefined }}>
            <TimePicker
              leftSection={<IoTimeOutline size={16} />}
              onChange={(time) => field.onChange(time || '')}
              value={field.value}
              error={!!fieldState.error}
              withSeconds={false}
              withDropdown
              format="12h"
              minutesStep={5}
              disabled={isAllDay}
              aria-label="Start time"
            />
            {fieldState.error?.message && (
              <Text c="red" style={errorTextStyles}>
                {fieldState.error.message}
              </Text>
            )}
          </Box>
        )}
      />

      <Text size="sm" c="dimmed">
        to
      </Text>

      <Controller
        name="endTime"
        control={control}
        rules={{
          required: !isAllDay ? 'End time is required' : false,
        }}
        render={({ field, fieldState }) => (
          <Box pos="relative" style={{ flex: isMobile ? 1 : undefined }}>
            <TimePicker
              leftSection={<IoTimeOutline size={16} />}
              onChange={(time) => field.onChange(time || '')}
              value={field.value}
              error={!!fieldState.error}
              withSeconds={false}
              withDropdown
              format="12h"
              disabled={isAllDay}
              minutesStep={5}
              aria-label="End time"
            />
            {fieldState.error?.message && (
              <Text c="red" style={errorTextStyles}>
                {fieldState.error.message}
              </Text>
            )}
          </Box>
        )}
      />
    </>
  );

  const submitButton = (
    <Button type="submit" loading={isFormSubmitting} fullWidth={isMobile}>
      ADD TO CALENDAR
    </Button>
  );

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack gap="md" py={8}>
        {isMobile ? (
          <>
            <Group gap="md" align="center">
              {dateRangeInput}
              {allDayCheckbox}
            </Group>
            <Group gap="md" align="center">
              {timeRangeInputs}
            </Group>
            {submitButton}
          </>
        ) : (
          <Group gap="md" align="center" wrap="nowrap">
            {dateRangeInput}
            {allDayCheckbox}
            {timeRangeInputs}
            {submitButton}
          </Group>
        )}
      </Stack>
    </form>
  );
}
